import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { subDays, subHours, subMonths, startOfDay, endOfDay, startOfMonth, endOfMonth, startOfWeek, subWeeks, endOfWeek, differenceInDays } from 'date-fns';
import { DropdownDateIntervalLabel, ButtonDateIntervalLabel, ErrorMessages } from '../../../constants';
import { Granularity, DateInterval } from '../../../enums';
import { ToastrService } from 'ngx-toastr';
import { DateIntervalFilterData } from '../../../models';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { isEmptyObject } from '../../../helpers/helpers';
import { FlatpickrDatePickerComponent } from '../../flatpickr-date-picker/flatpickr-date-picker.component';

export type DateIntervalOption = {
  value: DateInterval;
  label: string;
};

@Component({
  selector: 'app-date-filter',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FlatpickrDatePickerComponent
  ],
  templateUrl: './date-filter.component.html',
  styleUrl: './date-filter.component.scss'
})
export class DateFilterComponent implements OnInit, OnChanges {
  @Input() defaultInterval: DateInterval = DateInterval.Month;
  @Input() disableCustomDateSelector: boolean = false;
  @Input() disabledIntervals: DateInterval[] = [];
  @Input() filter: DateIntervalFilterData;
  @Input() isButtonIntervalSelector: boolean = false;
  @Input() datePickerPlacement: 'top' | 'bottom' = 'bottom';
  @Input() dateIntervalOptions: DateIntervalOption[] | null = null;
  @Output() filterChanged: EventEmitter<DateIntervalFilterData> = new EventEmitter<DateIntervalFilterData>();

  customGranularity: string;
  showDatePicker: boolean = false;
  isDropdownOpen: boolean = false;
  selectedDate: Date;
  intervalLabel: string;

  dateIntervalDropdownOptions: DateIntervalOption[] = [];
  dateIntervalButtons: DateIntervalOption[] = [];

  fromDate: Date | null = null;
  toDate: Date | null = null;
  isCustomDateSelected: boolean = false;

  private readonly defaultDateIntervalDropdownOptions: DateIntervalOption[] = [
    { value: DateInterval.Day, label: DropdownDateIntervalLabel[DateInterval.Day] },
    { value: DateInterval.Week, label: DropdownDateIntervalLabel[DateInterval.Week] },
    { value: DateInterval.Month, label: DropdownDateIntervalLabel[DateInterval.Month] },
    { value: DateInterval.Half, label: DropdownDateIntervalLabel[DateInterval.Half] },
  ];

  private readonly defaultDateIntervalButtons: DateIntervalOption[] = [
    { value: DateInterval.Day, label: ButtonDateIntervalLabel[DateInterval.Day] },
    { value: DateInterval.Week, label: ButtonDateIntervalLabel[DateInterval.Week] },
    { value: DateInterval.Month, label: ButtonDateIntervalLabel[DateInterval.Month] },
    { value: DateInterval.Half, label: ButtonDateIntervalLabel[DateInterval.Half] },
  ];

  private elementRef = inject(ElementRef<HTMLElement>);

  constructor(
    private toastr: ToastrService,
  ) {
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.elementRef.nativeElement.contains(event.target as Node)) {
      return;
    }

    this.closeCustomDateUi();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dateIntervalOptions'] && !changes['dateIntervalOptions'].firstChange) {
      this.configureIntervalOptions();
    }

    this.refreshIntervalLabel();
  }

  ngOnInit(): void {
    this.configureIntervalOptions();
    this.init();
  }

  init(): void {
    // Use filter if it's complete
    // NOTE: It will only be complete if the cached date is a custom date.
    // Otherwise, only the interval and granularity is pulled from the cache
    // to ensure we use the updated dates when refreshing the page
    if (!isEmptyObject(this.filter)
      && this.filter?.startDate !== undefined
      && this.filter?.endDate !== undefined) {
      return;
    }

    // Set default if the filter is empty
    if (isEmptyObject(this.filter)) {
      this.filter = {} as DateIntervalFilterData;
      this.filter.interval = this.defaultInterval;
    }

    this.setIntervalRange(this.filter.interval);
    this.refreshIntervalLabel();

    setTimeout(() => {
      this.updateFilter();
    }, 1);
  }

  // TODO: Refactor the logic to auto place the datepicker automatically based on screen size and current viewport position
  get isDatePickerTop(): boolean {
    return this.datePickerPlacement === 'top';
  }

  toggleDatePicker(): void {
    this.showDatePicker = !this.showDatePicker;
    this.isCustomDateSelected = this.showDatePicker || (this.filter?.isCustomDate ?? false);
    this.isDropdownOpen = false;
  }

  get isCustomDate(): boolean {
    return this.filter?.isCustomDate;
  }

  selectInterval(interval: DateInterval): void {
    this.isDropdownOpen = false;
    this.isCustomDateSelected = false;
    this.toDate = null;
    this.fromDate = null;
    if (interval != this.filter.interval || this.filter.isCustomDate) {
      this.filter.interval = interval;
      this.setIntervalRange(interval);
      this.refreshIntervalLabel();
    }

    if (this.showDatePicker) {
      this.showDatePicker = false;
    }

    this.updateFilter();
  }

  setDayInterval(): void {
    this.filter.isCustomDate = false;
    const today = new Date();
    this.filter.startDate = startOfDay(today);
    this.filter.endDate = endOfDay(today);
    this.filter.previousCycleStartDate = startOfDay(subDays(this.filter.startDate, 1));
    this.filter.previousCycleEndDate = endOfDay(subDays(this.filter.endDate, 1));
    this.filter.granularity = Granularity.Hourly;
  }

  setWeekInterval(): void {
    this.filter.isCustomDate = false;
    const today = new Date();
    this.filter.startDate = startOfWeek(today, { weekStartsOn: 1 });
    this.filter.endDate = endOfWeek(today, { weekStartsOn: 1});
    this.filter.previousCycleStartDate = startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
    this.filter.previousCycleEndDate = endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
    this.filter.granularity = Granularity.Daily;
  }

  setMonthInterval(): void {
    this.filter.isCustomDate = false;
    const today = new Date();
    this.filter.startDate = startOfMonth(today);
    this.filter.endDate = endOfMonth(today);
    this.filter.previousCycleStartDate = startOfMonth(subMonths(this.filter.startDate, 1));
    this.filter.previousCycleEndDate = endOfMonth(subMonths(this.filter.endDate, 1));
    this.filter.granularity = Granularity.Daily;
  }

  setHalfYearInterval(): void {
    this.filter.isCustomDate = false;
    const today = new Date();
    this.filter.startDate = startOfMonth(subMonths(today, 6));
    this.filter.endDate = endOfMonth(today);
    this.filter.previousCycleStartDate = startOfMonth(subMonths(this.filter.startDate, 6));
    this.filter.previousCycleEndDate = endOfMonth(subMonths(this.filter.endDate, 6));
    this.filter.granularity = Granularity.Monthly;
  }

  setLastHoursInterval(hours: number): void {
    this.filter.isCustomDate = false;
    const now = new Date();
    this.filter.startDate = subHours(now, hours);
    this.filter.endDate = now;
    this.filter.previousCycleStartDate = subHours(this.filter.startDate, hours);
    this.filter.previousCycleEndDate = subHours(this.filter.endDate, hours);
    this.filter.granularity = Granularity.Hourly;
  }

  setTodayInterval(): void {
    this.filter.isCustomDate = false;
    const today = new Date();
    this.filter.startDate = startOfDay(today);
    this.filter.endDate = endOfDay(today);
    this.filter.previousCycleStartDate = startOfDay(subDays(today, 1));
    this.filter.previousCycleEndDate = endOfDay(subDays(today, 1));
    this.filter.granularity = Granularity.Hourly;
  }

  setYesterdayInterval(): void {
    this.filter.isCustomDate = false;
    const yesterday = subDays(new Date(), 1);
    this.filter.startDate = startOfDay(yesterday);
    this.filter.endDate = endOfDay(yesterday);
    this.filter.previousCycleStartDate = startOfDay(subDays(yesterday, 1));
    this.filter.previousCycleEndDate = endOfDay(subDays(yesterday, 1));
    this.filter.granularity = Granularity.Hourly;
  }

  isOneDayDifference(firstDate: Date, secondDate: Date): boolean {
    const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
    const differenceInMilliseconds = Math.abs(firstDate.getTime() - secondDate.getTime());
    return differenceInMilliseconds === oneDayInMilliseconds;
  }

  refreshIntervalLabel() {
    if (this.filter?.interval !== null && this.filter?.interval !== undefined) {
      this.intervalLabel = this.getIntervalLabel();
    }
  }

  getIntervalLabel() {
    if (this.filter?.isCustomDate && this.filter?.startDate && this.filter?.endDate) {
      return `${this.filter.startDate!.toLocaleDateString('en-GB')} - ${this.filter.endDate!.toLocaleDateString('en-GB')}`;
    }

    const intervalOptions = this.isButtonIntervalSelector ? this.dateIntervalButtons : this.dateIntervalDropdownOptions;
    const selectedInterval = intervalOptions.find(x => x.value == this.filter.interval);

    return selectedInterval?.label ?? DropdownDateIntervalLabel[this.filter.interval];
  }

  updateFilter(): void {
    this.filterChanged.emit(this.filter);
  }

  toggleShowDatePicker(event: MouseEvent): void {
    this.showDatePicker = !this.showDatePicker;
    this.isCustomDateSelected = this.showDatePicker || (this.filter?.isCustomDate ?? false);
    this.isDropdownOpen = false;
    this.refreshIntervalLabel();
  }

  toggleDropdown(event?: MouseEvent): void {
    event?.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;

    if (this.isDropdownOpen) {
      this.showDatePicker = false;
      this.isCustomDateSelected = this.filter?.isCustomDate ?? false;
    }
  }

  onRangeSelected(selectedDates: Date[]): void {
    if (selectedDates.length === 2) {
      this.fromDate = selectedDates[0];
      this.toDate = selectedDates[1];

      const daysDiff = differenceInDays(this.toDate, this.fromDate);
      if (daysDiff > 31) {
        this.toastr.error(ErrorMessages['datepicker/date-range-one-month-max'], 'Invalid Date');
        this.fromDate = null;
        this.toDate = null;
        return;
      }

      this.filter.isCustomDate = true;
      this.filter.interval = this.defaultInterval;
      this.filter.startDate = startOfDay(this.fromDate);
      this.filter.endDate = endOfDay(this.toDate);

      const dateDiff = differenceInDays(this.filter.endDate, this.filter.startDate) + 1;
      this.filter.previousCycleStartDate = startOfDay(subDays(this.filter.startDate, dateDiff));
      this.filter.previousCycleEndDate = endOfDay(subDays(this.filter.endDate, dateDiff));

      this.refreshIntervalLabel();
      this.filter.granularity = Granularity.Daily;
      if (this.isOneDayDifference(this.filter.endDate, this.filter.startDate)) {
        this.filter.granularity = Granularity.Hourly;
        this.filter.interval = DateInterval.Day;
      }

      this.closeCustomDateUi();
      this.updateFilter();
    }
  }

  onRangeClear(): void {
    this.fromDate = null;
    this.toDate = null;
    this.closeCustomDateUi(true);
  }

  private configureIntervalOptions(): void {
    const dropdownOptions = this.dateIntervalOptions?.length
      ? this.dateIntervalOptions
      : this.defaultDateIntervalDropdownOptions;
    const buttonOptions = this.dateIntervalOptions?.length
      ? this.dateIntervalOptions
      : this.defaultDateIntervalButtons;

    this.dateIntervalDropdownOptions = dropdownOptions.filter(interval => !this.disabledIntervals.some(disabledInterval => disabledInterval === interval.value));
    this.dateIntervalButtons = buttonOptions.filter(interval => !this.disabledIntervals.some(disabledInterval => disabledInterval === interval.value));
  }

  private setIntervalRange(interval: DateInterval): void {
    if (interval == DateInterval.Day) {
      this.setDayInterval();
    } else if (interval == DateInterval.Week) {
      this.setWeekInterval();
    } else if (interval == DateInterval.Month) {
      this.setMonthInterval();
    } else if (interval == DateInterval.Half) {
      this.setHalfYearInterval();
    } else if (interval == DateInterval.Last3Hours) {
      this.setLastHoursInterval(3);
    } else if (interval == DateInterval.Last6Hours) {
      this.setLastHoursInterval(6);
    } else if (interval == DateInterval.Last12Hours) {
      this.setLastHoursInterval(12);
    } else if (interval == DateInterval.Today) {
      this.setTodayInterval();
    } else if (interval == DateInterval.Yesterday) {
      this.setYesterdayInterval();
    }
  }

  private closeCustomDateUi(resetCustomSelection: boolean = false): void {
    this.showDatePicker = false;
    this.isDropdownOpen = false;
    this.isCustomDateSelected = resetCustomSelection ? false : (this.filter?.isCustomDate ?? false);
  }
}